<template>
  <base-modal ref="modal" size="5xl">
    <base-card class="tw-overlow-auto tw-relative">
      <base-content id="printAgreement">
        <h1 class="tw-mb-4 tw-text-center tw-text-xl">Subscription Agreement</h1>
        <div class="tw-grid tw-grid-cols-5 tw-gap-2">
          <div class="tw-text-gray-800 tw-text-xs tw-font-normal">Services</div>
          <div class="tw-col-span-4">
            <div v-for="product in selectedProducts" :key="product.productId" class="tw-mb-2">
              <div class="tw-mb-1">
                <div class="tw-text-gray-800 tw-text-sm tw-font-normal">{{ product.productName }}</div>
              </div>
              <div class="tw-flex" v-if="product.flatAmount">
                <Money-Format :value="product.flatAmount" :locale="locale" :currency-code="product.currency" :subunits-value="true"></Money-Format
                ><span class="tw-ml-1">each {{ product.intervalCount }} {{ product.interval }}</span>
              </div>
              <div v-if="!product.flatAmount">
                <div v-for="(tier, i) in product.tiers" :key="tier.upTo" class="tw-text-sm tw-mb-0.5 tw-grid tw-grid-cols-3">
                  <div v-if="i === 0" class="tw-flex tw-text-sm">First {{ tier.upTo }} plans</div>
                  <div v-if="i > 0 && tier.upTo" class="tw-flex tw-text-sm">Plans {{ product.tiers[i - 1].upTo + 1 }} up to {{ tier.upTo }}</div>
                  <div v-if="i > 0 && !tier.upTo" class="tw-flex tw-text-sm">Plans {{ product.tiers[i - 1].upTo + 1 }} and up</div>
                  <div class="tw-col-span-2">
                    <div v-if="tier.flatAmount">
                      <Money-Format :value="tier.flatAmount" :locale="locale" :currency-code="product.currency" :subunits-value="true"></Money-Format>
                    </div>
                    <div class="tw-inline-flex" v-else>
                      <Money-Format :value="tier.unitAmount" :locale="locale" :currency-code="product.currency" :subunits-value="true"></Money-Format
                      ><span class="tw-ml-1">/ plan</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="tw-grid tw-grid-cols-5 tw-gap-2 tw-mb-2">
          <div class="tw-text-gray-800 tw-text-xs tw-font-normal">Agreement Start Date</div>
          <div class="tw-col-span-4 tw-text-gray-800 tw-text-xs">
            <div>
              This Agreement is effective on the <b>Date of Acceptance</b> by <b>{{ businessName }}</b
              >, and includes all Services and respective Fees listed above.
            </div>
          </div>
        </div>
        <div class="tw-grid tw-grid-cols-5 tw-gap-2 tw-mb-2">
          <div class="tw-text-gray-800 tw-text-xs tw-font-normal">Service Start Date</div>
          <div class="tw-col-span-4 tw-text-gray-800 tw-text-xs">
            <div>Agreement Start Date or the first day on which the Business started using Predictile, whichever is earlier.</div>
          </div>
        </div>
        <div class="tw-grid tw-grid-cols-5 tw-gap-2 tw-mb-2">
          <div class="tw-text-gray-800 tw-text-xs tw-font-normal">Agreement Term</div>
          <div class="tw-col-span-4 tw-text-gray-800 tw-text-xs">
            <div>Monthly - cancel anytime</div>
          </div>
        </div>
        <div class="tw-grid tw-grid-cols-5 tw-gap-2 tw-mb-2">
          <div class="tw-text-gray-800 tw-text-xs tw-font-normal">Agreement End Date</div>
          <div class="tw-col-span-4 tw-text-gray-800 tw-text-xs">
            <div>Agreement End Date is at the end of each month and auto-renews until the Client cancels the Business Agreement.</div>
          </div>
        </div>
        <div class="tw-grid tw-grid-cols-5 tw-gap-2 tw-mb-2">
          <div class="tw-text-gray-800 tw-text-xs tw-font-normal">Billing Cycle</div>
          <div class="tw-col-span-4 tw-text-gray-800 tw-text-xs">
            <div>
              Predictile Basic is charged at end of each month (per the volume-based graduated price) based on the highest number of Member Plans managed on the platform each day
              of the respective calendar month. Predictile "Add-On" product suites are charged at the beginning of each month at their respective monthly price. The first month
              charge may be pro-rated if started during the month.
            </div>
          </div>
        </div>
        <div class="tw-grid tw-grid-cols-5 tw-gap-2 tw-mb-2">
          <div class="tw-text-gray-800 tw-text-xs tw-font-normal">Payment Terms</div>
          <div class="tw-col-span-4 tw-text-gray-800 tw-text-xs">
            <div>
              All payments are processed via ACH, Debit, or Credit Card on your Account based on the Billing Cycle. For multiple services and product suites, there will be one (1)
              charge, but the invoice will itemize each service. For one-time charges, those charges will be included and itemized, as well.
            </div>
            <div class="tw-mt-2">
              Any Declined charges will incur a $25 declined fee, and you will have a five (5) day Grace Period to update the Payment method successfully. After the Grace Period,
              if the Declined issue still persists then your Account and Predictile Platform access will be placed On-Hold in "Past Due" status until the payment is updated
              successfully. During "Past Due" status, no usage of the Predictile Platform will be permitted. Upon successful update of Payment method, all outstanding charges will
              be processed to bring your account to current and permission to the Platform will be restored.
            </div>
          </div>
        </div>
        <div class="tw-grid tw-grid-cols-5 tw-gap-2 tw-mb-2">
          <div class="tw-text-gray-800 tw-text-xs tw-font-normal">Auto-Renewal Terms</div>
          <div class="tw-col-span-4 tw-text-gray-800 tw-text-xs">
            In order to avoid service disruption, at the end of each monthly Agreement Term the agreement will be automatically renewed for a period of another monthly Agreement
            Term unless modified or cancelled by written notice (email or letter) from the Client at least 15 days prior to the current Agreement End Date.
          </div>
        </div>
        <div class="tw-grid tw-grid-cols-5 tw-gap-2 tw-mb-2">
          <div class="tw-text-gray-800 tw-text-xs tw-font-normal">Non-Renewal Terms</div>
          <div class="tw-col-span-4 tw-text-gray-800 tw-text-xs">
            For a Business Agreement cancelled at the end of its current monthly Agreement period, the Client will have a five (5) day Grace Period from the then current Agreement
            End Date to transition Client content data. After the Grace Period, the Account will be closed, Client data will no longer be actively maintained nor accessible, and
            the Agreement will be terminated. For Predictile Equipping services where the cost is on a payment plan over multiple months, any outstanding balance is due in full
            upon cancellation of the Business Agreement for Predictile Basic services.
          </div>
        </div>
        <div class="tw-grid tw-grid-cols-5 tw-gap-2 tw-mb-2">
          <div class="tw-text-gray-800 tw-text-xs tw-font-normal">Early Termination</div>
          <div class="tw-col-span-4 tw-text-gray-800 tw-text-xs">
            Predictile Basic and all Add-On services are monthly terms and can be canceled anytime, so there is no early termination. For Predictile Equipping services where the
            cost is on a payment plan over multiple months, any outstanding balance is due in full upon cancellation of the Business Agreement for Predictile Basic services.
          </div>
        </div>
        <div class="tw-grid tw-grid-cols-5 tw-gap-2 tw-mb-2">
          <div class="tw-text-gray-800 tw-text-xs tw-font-normal">Date of Acceptance</div>
          <div class="tw-col-span-4 tw-text-gray-800 tw-text-xs">
            This agreement was electronically accepted by <b>{{ subscription.agreementBy }}</b> on <b>{{ customDateFormatter(subscription.acceptedDate) }}</b
            >.
          </div>
        </div>
      </base-content>
      <div class="tw-flex tw-justify-end tw-gap-2">
        <base-button type="flat" @click="hide">Close</base-button>
        <base-button type="secondary" @click="printAgreement('printAgreement', businessName)">Print</base-button>
      </div>
    </base-card>
  </base-modal>
</template>

<script>
import MoneyFormat from "vue-money-format";
import moment from "moment";

export default {
  name: "BusinessMemberPlanAgreement",
  components: { MoneyFormat },
  props: {
    businessId: String,
    businessName: String,
  },
  data() {
    return {
      subscription: {},
      selectedProducts: [],
    };
  },
  computed: {
    locale() {
      return window.navigator.language;
    },
    formattedPrice() {
      return new Intl.NumberFormat(this.locale, { style: "currency", currency: "USD" }).format(this.priceInCents / 100);
    },
    linkStyle() {
      return {
        color: this.secondaryColor,
      };
    },
  },
  methods: {
    show(subscription) {
      this.subscription = subscription;
      this.selectedProducts = subscription.items;
      this.$refs.modal.show();
    },
    hide() {
      this.$refs.modal.hide();
    },
    printAgreement(agreement, businessName) {
      var printContents = document.getElementById(agreement).innerHTML;
      var originalContents = document.body.innerHTML;
      document.body.innerHTML = businessName + " Subscription Agreement" + printContents;
      window.print();
      document.body.innerHTML = originalContents;
      location.reload();
    },
    customDateFormatter(date) {
      return moment
        .utc(date)
        .local()
        .format("MMM D, YYYY hh:mm A");
    },
  },
};
</script>
